import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Button, Card, Spin, message, Modal, QRCode, Alert } from 'antd'
import {
    CheckCircleOutlined,
    CloseOutlined,
    LoadingOutlined,
    CheckCircleFilled,
    CloseCircleFilled,
    ClockCircleOutlined
} from '@ant-design/icons'
import { listAllVipPackages, createAlipayQROrder, queryAlipayOrder, bindVipQuestionMark } from '../../api/wordcloud'
import styles from './VipPurchaseModal.module.css'

// 支付状态
const PAYMENT_STATUS = {
    IDLE: 'idle',
    LOADING_PACKAGES: 'loading_packages',
    SELECT_PACKAGE: 'select_package',
    CREATING: 'creating',
    PENDING: 'pending',
    SUCCESS: 'success',
    CLOSED: 'closed',
    ERROR: 'error'
}

export default function VipPurchaseModal({ open, onClose, onSuccess }) {
    const [packages, setPackages] = useState([])
    const [selectedPackage, setSelectedPackage] = useState(null)
    const [paymentStatus, setPaymentStatus] = useState(PAYMENT_STATUS.IDLE)
    const [qrCode, setQrCode] = useState('')
    const [outTradeNo, setOutTradeNo] = useState('')
    const [countdown, setCountdown] = useState(15 * 60) // 15分钟倒计时
    const pollIntervalRef = useRef(null)
    const timeoutRef = useRef(null)
    const countdownRef = useRef(null)

    // 获取套餐列表
    const fetchPackages = useCallback(async () => {
        try {
            setPaymentStatus(PAYMENT_STATUS.LOADING_PACKAGES)
            const allPackages = await listAllVipPackages()
            // 只显示 essay_count 大于 0 的套餐（作文批改套餐）
            const essayPackages = allPackages.filter(pkg => pkg.essay_count > 0)
            setPackages(essayPackages)
            if (essayPackages.length > 0) {
                setSelectedPackage(essayPackages[0])
            }
            setPaymentStatus(PAYMENT_STATUS.SELECT_PACKAGE)
        } catch (err) {
            console.error('获取套餐列表失败:', err)
            message.error('获取套餐列表失败，请刷新重试')
            setPaymentStatus(PAYMENT_STATUS.ERROR)
        }
    }, [])

    // 当弹窗打开时获取套餐
    useEffect(() => {
        if (open) {
            fetchPackages()
        }
    }, [open, fetchPackages])

    // 清除所有定时器
    const clearAllTimers = useCallback(() => {
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current)
            pollIntervalRef.current = null
        }
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
            timeoutRef.current = null
        }
        if (countdownRef.current) {
            clearInterval(countdownRef.current)
            countdownRef.current = null
        }
    }, [])

    // 清理定时器
    useEffect(() => {
        return () => clearAllTimers()
    }, [clearAllTimers])

    // 重置状态
    const resetState = useCallback(() => {
        clearAllTimers()
        setPaymentStatus(PAYMENT_STATUS.IDLE)
        setQrCode('')
        setOutTradeNo('')
        setPackages([])
        setSelectedPackage(null)
    }, [clearAllTimers])

    // 关闭弹窗
    const handleClose = useCallback(() => {
        resetState()
        onClose()
    }, [resetState, onClose])

    // 格式化倒计时
    const formatCountdown = (seconds) => {
        const mins = Math.floor(seconds / 60)
        const secs = seconds % 60
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }

    // 开始轮询订单状态
    const startPolling = useCallback((tradeNo) => {
        let pollCount = 0

        pollIntervalRef.current = setInterval(async () => {
            try {
                pollCount++
                console.log(`查询订单状态 (${pollCount}):`, tradeNo)
                const orderData = await queryAlipayOrder(tradeNo)

                if (orderData.status === 'paid') {
                    // 支付成功
                    clearAllTimers()
                    setPaymentStatus(PAYMENT_STATUS.SUCCESS)

                    // 激活套餐：订单查询接口会返回该笔订单归属的 user_id，以此为准，
                    // localStorage 中的 user_id 仅作兜底
                    const packageId = localStorage.getItem('pending_package_id')
                    const userId = orderData.user_id ?? localStorage.getItem('user_id')
                    if (packageId && userId) {
                        try {
                            await bindVipQuestionMark(Number(userId), Number(packageId))
                            localStorage.removeItem('pending_package_id')
                            message.success('支付成功，套餐已激活！')

                            // 延迟一下再关闭，让用户看到成功提示
                            setTimeout(() => {
                                resetState()
                                onSuccess()
                            }, 1500)
                        } catch (err) {
                            console.error('激活套餐失败:', err)
                            message.warning('支付成功，但激活套餐时出现问题，请联系客服')
                            setTimeout(() => {
                                resetState()
                                onSuccess()
                            }, 1500)
                        }
                    } else {
                        // 即使没有 user_id 也关闭
                        setTimeout(() => {
                            resetState()
                            onSuccess()
                        }, 1500)
                    }
                } else if (orderData.status === 'closed') {
                    // 订单关闭
                    clearAllTimers()
                    setPaymentStatus(PAYMENT_STATUS.CLOSED)
                    message.error('订单已关闭，请重新购买')
                }
                // pending 状态继续轮询
            } catch (error) {
                console.error('查询订单失败', error)
                // 查询失败不停止轮询，继续尝试
            }
        }, 2000) // 每2秒查询一次
    }, [clearAllTimers, resetState, onSuccess])

    // 开始倒计时
    const startCountdown = useCallback(() => {
        setCountdown(15 * 60)
        countdownRef.current = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    clearInterval(countdownRef.current)
                    return 0
                }
                return prev - 1
            })
        }, 1000)
    }, [])

    // 处理购买
    const handlePurchase = useCallback(async (pkg) => {
        if (!pkg) return

        try {
            setPaymentStatus(PAYMENT_STATUS.CREATING)
            clearAllTimers()

            // 创建支付宝扫码支付订单
            const data = await createAlipayQROrder(pkg.price, pkg.desc)

            if (data.qr_code && data.out_trade_no) {
                setQrCode(data.qr_code)
                setOutTradeNo(data.out_trade_no)
                setPaymentStatus(PAYMENT_STATUS.PENDING)

                // 保存套餐 ID
                localStorage.setItem('pending_package_id', String(pkg.id))

                // 开始轮询
                startPolling(data.out_trade_no)

                // 开始倒计时
                startCountdown()

                // 设置超时（15分钟）
                timeoutRef.current = setTimeout(() => {
                    clearAllTimers()
                    setPaymentStatus(PAYMENT_STATUS.CLOSED)
                    message.warning('二维码已过期，请重新获取')
                }, 15 * 60 * 1000)
            } else {
                throw new Error('返回数据格式错误')
            }
        } catch (error) {
            console.error('创建订单失败', error)
            setPaymentStatus(PAYMENT_STATUS.ERROR)
            message.error('创建订单失败，请稍后重试')
        }
    }, [clearAllTimers, startPolling, startCountdown])

    // 重新获取二维码
    const handleRefreshQR = useCallback(() => {
        if (selectedPackage) {
            handlePurchase(selectedPackage)
        }
    }, [selectedPackage, handlePurchase])

    // 返回选择套餐
    const handleBackToPackages = useCallback(() => {
        clearAllTimers()
        setPaymentStatus(PAYMENT_STATUS.SELECT_PACKAGE)
        setQrCode('')
        setOutTradeNo('')
    }, [clearAllTimers])

    // 渲染内容
    const renderContent = () => {
        if (paymentStatus === PAYMENT_STATUS.LOADING_PACKAGES) {
            return (
                <div className={styles.loading_container}>
                    <Spin size="small" />
                    <span className={styles.loading_text}>加载套餐中...</span>
                </div>
            )
        }

        if (paymentStatus === PAYMENT_STATUS.SELECT_PACKAGE) {
            return (
                <div className={styles.packages_container}>
                    <div className={styles.packages_list}>
                        {packages.map((pkg, index) => (
                            <div
                                key={pkg.id}
                                className={`${styles.package_item} ${selectedPackage?.id === pkg.id ? styles.selected : ''}`}
                                onClick={() => setSelectedPackage(pkg)}
                            >
                                <div className={styles.package_name}>{pkg.desc}</div>
                                <div className={styles.package_info}>
                                    <span className={styles.package_count}>{pkg.essay_count}次</span>
                                    <span className={styles.package_price}>¥{(pkg.price / 100).toFixed(2)}</span>
                                </div>
                                {selectedPackage?.id === pkg.id && (
                                    <CheckCircleOutlined className={styles.selected_icon} />
                                )}
                            </div>
                        ))}
                    </div>
                    <Button
                        type="primary"
                        size="small"
                        className={styles.buy_btn}
                        disabled={!selectedPackage}
                        onClick={() => handlePurchase(selectedPackage)}
                    >
                        立即购买
                    </Button>
                </div>
            )
        }

        if (paymentStatus === PAYMENT_STATUS.CREATING) {
            return (
                <div className={styles.payment_container}>
                    <Spin size="small" />
                    <span className={styles.payment_text}>正在创建订单...</span>
                </div>
            )
        }

        if (paymentStatus === PAYMENT_STATUS.PENDING) {
            return (
                <div className={styles.payment_container}>
                    <div className={styles.qr_code_wrapper}>
                        <QRCode
                            value={qrCode}
                            size={140}
                            status={countdown > 0 ? 'active' : 'expired'}
                        />
                    </div>
                    <Alert
                        message="请使用支付宝扫码支付"
                        type="info"
                        showIcon
                        className={styles.pay_hint}
                    />
                    <div className={styles.order_info}>
                        <span className={styles.order_label}>订单号：</span>
                        <span className={styles.order_no}>{outTradeNo}</span>
                    </div>
                    <div className={styles.countdown}>
                        <ClockCircleOutlined />
                        <span>
                            有效期：{formatCountdown(countdown)}
                            {countdown <= 0 && ' (已过期)'}
                        </span>
                    </div>
                    {countdown <= 0 && (
                        <Button
                            type="primary"
                            size="small"
                            onClick={handleRefreshQR}
                            className={styles.refresh_btn}
                        >
                            重新获取
                        </Button>
                    )}
                    <div className={styles.pending_hint}>
                        <LoadingOutlined spin />
                        <span>等待支付...</span>
                    </div>
                    <Button
                        size="small"
                        onClick={handleBackToPackages}
                        className={styles.back_btn}
                    >
                        重新选择套餐
                    </Button>
                </div>
            )
        }

        if (paymentStatus === PAYMENT_STATUS.SUCCESS) {
            return (
                <div className={styles.payment_container}>
                    <CheckCircleFilled className={styles.success_icon} />
                    <div className={styles.success_text}>支付成功！</div>
                </div>
            )
        }

        if (paymentStatus === PAYMENT_STATUS.CLOSED) {
            return (
                <div className={styles.payment_container}>
                    <CloseCircleFilled className={styles.closed_icon} />
                    <div className={styles.closed_text}>订单已关闭</div>
                    <div className={styles.button_group}>
                        <Button type="primary" size="small" onClick={handleRefreshQR}>
                            重新购买
                        </Button>
                        <Button size="small" onClick={handleBackToPackages}>
                            重新选择
                        </Button>
                    </div>
                </div>
            )
        }

        if (paymentStatus === PAYMENT_STATUS.ERROR) {
            return (
                <div className={styles.payment_container}>
                    <CloseCircleFilled className={styles.error_icon} />
                    <div className={styles.error_text}>创建订单失败</div>
                    <div className={styles.button_group}>
                        <Button type="primary" size="small" onClick={handleRefreshQR}>
                            重试
                        </Button>
                        <Button size="small" onClick={handleBackToPackages}>
                            返回
                        </Button>
                    </div>
                </div>
            )
        }

        return null
    }

    return (
        <Modal
            open={open}
            onCancel={handleClose}
            footer={null}
            closable={true}
            closeIcon={<CloseOutlined />}
            width={320}
            centered
            className={styles.vip_modal}
            title={
                paymentStatus === PAYMENT_STATUS.SELECT_PACKAGE ? '选择套餐' :
                paymentStatus === PAYMENT_STATUS.PENDING ? '扫码支付' :
                null
            }
        >
            {renderContent()}
        </Modal>
    )
}